import { memo, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  AVAILABILITY_LOG_TYPE,
  BUTTON_VARIANTS,
  COLORS,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  DATE_TIME_FORMATS,
  INPUT_TYPES,
  TIME_UNITS,
} from "@miplatform/constants";
import { addToDate, getDate, getDateISO } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiClipboardAccountOutline } from "@mdi/js";
import { Collapse } from "@mui/material";
import { useTheme } from "@mui/system";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro";
import { MiCheckbox } from "../MiCheckbox/MiCheckbox";
import { MiDatePicker } from "../MiDatePicker/MiDatePicker";
import { MiDateRangePicker } from "../MiDateRangePicker/MiDateRangePicker";
import { MiDetailFields } from "../MiDetailsFields/MiDetailsFields";
import { MiModal } from "../MiModal/MiModal";
import { useMarkInactiveViewModel } from "./useMarkInactiveViewModel";

const markInactiveSchema = z.object({
  note: z.string().min(1, "Reason is required."),
});

/**
 * MarkInactiveModal component - A modal for marking a user as inactive
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Object} props.calendarDate - The calendar date for the inactive period
 * @param {Object} props.user - The user object to mark as inactive
 * @param {string} props.user.id - User's unique identifier
 * @param {string} [props.user.name] - User's display name
 * @param {Function} props.onRefresh - Callback function to refresh data after marking user inactive
 * @param {Function} props.onClose - Callback function to close the modal
 * @returns {JSX.Element} The MarkInactiveModal component
 */
export const MarkInactiveModal = memo(
  ({ open, calendarDate, user, onRefresh, onClose }) => {
    const theme = useTheme();
    const {
      control,
      reset,
      clearErrors,
      setError,
      setValue,
      getValues,
      watch,
      formState: { isValid },
    } = useForm({
      mode: "onChange",
      defaultValues: {
        date: getDate(calendarDate),
      },
      resolver: zodResolver(markInactiveSchema),
    });

    const isAllDay = watch("isAllDay");
    const isSpecificTime = watch("isSpecificTime");
    const date = watch("date");
    const [dateRangeState, setDateRangeState] = useState();

    const { isLoading, createUserLog } = useMarkInactiveViewModel({
      user,
      onClose,
      onRefresh,
    });

    const handleTimeValidation = (time, label, key) => {
      if (!time?.isValid()) {
        setError(key, { message: `${label} is required.` });
      } else {
        setValue(key, time);
        clearErrors(key);
      }
    };

    const markInactiveFields = [
      {
        label: "Date",
        name: isAllDay ? "dateRange" : "date",
        width: "100%",
        fieldType: INPUT_TYPES.CUSTOM,
        required: true,
        formControl: control,
        strictInterval: true,
        timeSteps: { minutes: 15 },
        customComponent: isAllDay ? (
          <MiDateRangePicker
            dateRange={dateRangeState}
            disablePast
            dateFormat={DATE_TIME_FORMATS.FORMAT_MMMM_D_YYYY}
            onDateChange={(startDate = getDate(), endDate = null) => {
              const newRange = {
                startDate: getDateISO(startDate),
                endDate: endDate ? getDateISO(endDate) : getDateISO(startDate),
              };
              setDateRangeState(newRange);
              setValue("dateRange", newRange, { shouldDirty: true });
              if (startDate) {
                setValue("date", startDate.toDate(), { shouldDirty: true });
              }
            }}
            calendarCount={2}
            slots={{ field: SingleInputDateRangeField }}
            slotProps={{
              actionBar: {
                actions: ["clear"],
              },
              textField: {
                size: "small",
                sx: {
                  width: "100%",
                  "& .MuiInputBase-root": {
                    padding: "0px 8px",
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "8.5px",
                  },
                },
              },
            }}
          />
        ) : (
          <MiDatePicker
            required
            disabled
            format={DATE_TIME_FORMATS.FORMAT_MMMM_D_YYYY}
            value={date}
            onChange={(date) => setValue("date", date, { shouldDirty: true })}
            variant={DATEPICKER_VARIANTS.DESKTOP}
            actions={[DATEPICKER_ACTIONS.CANCEL]}
          />
        ),
      },
      {
        label: "All Day",
        name: "isAllDay",
        value: isAllDay,
        formControl: control,
        fieldType: INPUT_TYPES.CUSTOM,
        width: "100%",
        customComponent: (
          <MiCheckbox
            name="isAllDay"
            label="All Day"
            value={isAllDay}
            handleChange={({ target: { checked } }) => {
              setValue("isAllDay", checked, { shouldDirty: true });
              if (checked) {
                setValue("isSpecificTime", false);
              }
            }}
          />
        ),
      },
      {
        label: "Specific Time",
        name: "isSpecificTime",
        value: isSpecificTime,
        formControl: control,
        fieldType: INPUT_TYPES.CUSTOM,
        width: "100%",
        customComponent: (
          <MiCheckbox
            name="isSpecificTime"
            label="Specific Time"
            value={isSpecificTime}
            handleChange={({ target: { checked } }) => {
              setValue("isSpecificTime", checked, { shouldDirty: true });
              if (checked) {
                setValue("isAllDay", false);
              }
            }}
          />
        ),
      },
    ];

    const timeFields = [
      {
        label: "Start Time",
        name: "from",
        width: "50%",
        fieldType: INPUT_TYPES.TIME_PICKER,
        format: DATE_TIME_FORMATS.FORMAT_12_HOUR,
        required: true,
        formControl: control,
        strictInterval: true,
        timeSteps: { minutes: 15 },
        onChange: (time) => handleTimeValidation(time, "Start Time", "from"),
      },
      {
        label: "End Time",
        name: "to",
        width: "50%",
        fieldType: INPUT_TYPES.TIME_PICKER,
        format: DATE_TIME_FORMATS.FORMAT_12_HOUR,
        required: true,
        formControl: control,
        strictInterval: true,
        timeSteps: { minutes: 15 },
        onChange: (time) => handleTimeValidation(time, "End Time", "to"),
      },
    ];

    const reasonField = [
      {
        label: "Reason",
        name: "note",
        required: true,
        formControl: control,
        width: "100%",
      },
    ];

    useEffect(() => {
      if (open) {
        const currentHour = getDate(calendarDate).startOf(TIME_UNITS.HOUR);
        setDateRangeState({
          startDate: getDateISO(calendarDate),
          endDate: getDateISO(calendarDate),
        });
        reset({
          date: getDate(calendarDate),
          from: currentHour,
          to: addToDate(currentHour, 1, TIME_UNITS.HOUR),
          note: "",
        });
      }
    }, [open, calendarDate]);

    const handleSubmit = () => {
      if (isAllDay) {
        const currentDateRange = getValues("dateRange") || dateRangeState;
        const startDate = currentDateRange.startDate
          ? getDate(currentDateRange.startDate)
          : getDate(calendarDate);
        const endDate = currentDateRange.endDate
          ? getDate(currentDateRange.endDate)
          : startDate;

        createUserLog({
          isAllDay: true,
          from: startDate
            .startOf("day")
            .format(DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT),
          to: endDate
            .clone()
            .endOf("day")
            .format(DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT),
          note: getValues("note"),
          userId: user.id,
          type: AVAILABILITY_LOG_TYPE.INACTIVE,
        });
      } else {
        createUserLog({
          isAllDay: false,
          from: getValues("from").format(
            DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
          ),
          to: getValues("to").format(
            DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
          ),
          note: getValues("note"),
          userId: user.id,
          type: AVAILABILITY_LOG_TYPE.INACTIVE,
        });
      }
    };

    return (
      <MiModal
        title={`Mark ${user?.name} Inactive`}
        titleIcon={mdiClipboardAccountOutline}
        leftIconColor={theme.palette.primary.main}
        open={open}
        closeModal={onClose}
        loading={isLoading}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: COLORS.BLACK,
            onClick: onClose,
          },
          {
            name: "Mark Inactive",
            variant: BUTTON_VARIANTS.CONTAINED,
            disabled: !isValid || (!isAllDay && !isSpecificTime) || isLoading,
            onClick: handleSubmit,
          },
        ]}
      >
        <MiDetailFields detailFields={markInactiveFields} />
        <Collapse defaultValue={false} in={isSpecificTime}>
          <MiDetailFields detailFields={timeFields} />
        </Collapse>
        <MiDetailFields detailFields={reasonField} />
      </MiModal>
    );
  }
);
